import React, { useEffect, useState } from 'react';
import { History, ChevronRight, RefreshCw } from 'lucide-react';
import { visitAPI } from '../services/api';
import { formatClinicDateTime } from '../utils/clinicTime';
// Labels and value rendering are shared with the Token Edits report so the two describe the same
// audit rows identically.
import { auditFieldLabel, formatAuditValue } from '../utils/visitAudit';

/**
 * Change history for a token, shown inside the visit details modal.
 *
 * Every write goes through VisitAuditLogService.runInBatch, so a batchId is always present and
 * grouping is a straight partition on it — one card per edit, listing every field that moved.
 * (User Management's timeline needs a fuzzier rule because it has legacy rows predating batching;
 * that's why the two aren't sharing a grouping function.)
 */

const groupByBatch = (log) => {
  const groups = [];
  for (const entry of log) {
    const prev = groups[groups.length - 1];
    if (prev && entry.batchId && prev.batchId === entry.batchId) {
      prev.changes.push(entry);
    } else {
      groups.push({
        key: entry.batchId || entry.id,
        batchId: entry.batchId || null,
        createdAt: entry.createdAt,
        changedByName: entry.changedByName,
        changes: [entry],
      });
    }
  }
  return groups;
};

const VisitAuditHistory = ({ visitId }) => {
  const [log, setLog] = useState(null); // null = not loaded yet
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!visitId) return undefined;
    let cancelled = false;
    setLoading(true);
    visitAPI.getAudit(visitId)
      .then((data) => { if (!cancelled) setLog(data || []); })
      .catch(() => { if (!cancelled) setLog([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [visitId]);

  const groups = log ? groupByBatch(log) : [];

  return (
    <div className="bg-gray-50 rounded-2xl p-6">
      <h3 className="font-bold text-gray-800 mb-4 flex items-center">
        <History className="h-5 w-5 mr-2 text-gray-600" />
        Change History
        {groups.length > 0 && (
          <span className="ml-2 text-sm font-medium text-gray-500">
            {groups.length} edit{groups.length !== 1 ? 's' : ''}
          </span>
        )}
      </h3>

      {loading && (
        <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
          <RefreshCw className="h-4 w-4 animate-spin" />
          Loading history...
        </div>
      )}

      {!loading && groups.length === 0 && (
        <p className="text-sm text-gray-500">
          This token has not been changed since it was issued.
        </p>
      )}

      {!loading && groups.length > 0 && (
        <div className="space-y-3">
          {groups.map((group) => (
            <div key={group.key} className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="flex items-start justify-between gap-3 mb-3">
                <p className="text-sm font-semibold text-gray-900">
                  {group.changes.length} field{group.changes.length !== 1 ? 's' : ''} changed
                </p>
                <div className="text-right shrink-0">
                  <p className="text-xs text-gray-500">{formatClinicDateTime(group.createdAt)}</p>
                  {group.changedByName && (
                    <p className="text-xs text-gray-700 font-medium mt-0.5">{group.changedByName}</p>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                {group.changes.map((entry) => (
                  <div key={entry.id} className="flex items-center flex-wrap gap-2 text-sm">
                    <span className="text-gray-600 w-28 shrink-0">
                      {auditFieldLabel(entry.fieldName)}
                    </span>
                    <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded-md font-mono text-xs">
                      {formatAuditValue(entry.oldValue, entry.fieldName)}
                    </span>
                    <ChevronRight className="h-3.5 w-3.5 text-gray-400" />
                    <span className={`px-2 py-0.5 rounded-md font-mono text-xs ${
                      entry.action === 'TOKEN_REISSUED'
                        ? 'bg-amber-100 text-amber-800'
                        : 'bg-emerald-100 text-emerald-800'
                    }`}>
                      {formatAuditValue(entry.newValue, entry.fieldName)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default VisitAuditHistory;
